import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import Typography from '@mui/material/Typography';
import { IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import "devextreme/dist/css/dx.light.css";
import DataGrid, {
  Column,
  Editing,
  Selection,
  RangeRule,
  Sorting,
} from "devextreme-react/data-grid";
import NoticeEmitter from '../../../../NoticeUtil';

const createEmptyRow = (id) => ({ id, type: '', length: '', width: '', height: '', weight: '', positions: '', quantity: '', description: '' });

const isRowEmpty = (row) => {
    if (!row) return true;
    return !row.type && !row.length && !row.width && !row.height && !row.weight && !row.positions && !row.quantity && !row.description;
}

function DefineIndividualPieces(props) {
  const dataGridRef = useRef(null);

  const [piecesOptionList, setPiecesOptionList] = useState(() => {
    const initialData = props.initialData || [];
    const maxId = initialData.length > 0 ? Math.max(...initialData.map(p => p.id)) : 0;
    return [...initialData, createEmptyRow(maxId + 1)];
  });

  useEffect(() => {
    const itemsToSync = piecesOptionList.filter(row => !isRowEmpty(row));
    props.setDefineIndividual(itemsToSync);
  }, [piecesOptionList, props.setDefineIndividual]);

  const onRowUpdated = (e) => {
    setPiecesOptionList(currentList => {
      const lastRowId = currentList[currentList.length - 1].id;
      const wasLastRow = e.key === lastRowId;

      const updatedList = currentList.map(item =>
        item.id === e.key ? { ...item, ...e.data } : item
      );

      if (wasLastRow) {
        const updatedRow = updatedList.find(item => item.id === e.key);
        if (updatedRow && !isRowEmpty(updatedRow)) {
          const newId = Math.max(...updatedList.map(r => r.id)) + 1;
          return [...updatedList, createEmptyRow(newId)];
        }
      }
      return updatedList;
    });
    NoticeEmitter.emit('PackagePiecesUpdate', 'update');
  };

  const onRowRemoved = (id) => {
    setPiecesOptionList(prev => prev.filter(obj => obj.id !== id));
    setTimeout(() => NoticeEmitter.emit('PackagePiecesUpdate', 'minus'), 500);
  }

  const onGridBlur = (e) => {
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    if (dataGridRef.current) {
      dataGridRef.current.instance().saveEditData();
    }
  };

  const renderDelete = params => (
    <IconButton
      color="primary"
      onClick={() => onRowRemoved(params.data.id)}
      disabled={isRowEmpty(params.data) && piecesOptionList.filter(r => isRowEmpty(r)).length === 1}
      tabIndex={-1}
    >
      <ClearIcon style={{ fontSize: 12 }}/>
    </IconButton>
  );

  const onCellPrepared = (e) => {
    // Mark Delete-column data cells as focus-disabled so DevExtreme's keyboard
    // nav skips them — without this Tab and arrow keys land on the X.
    if (e.rowType === "data" && !e.column?.dataField) {
      e.cellElement.classList.add('dx-cell-focus-disabled');
      e.cellElement.setAttribute('tabindex', '-1');
    }
    if (e.rowType === "data" && typeof e.value === "number" && e.value < 0) {
      e.cellElement.style.setProperty('color', 'red', 'important');
      e.cellElement.style.setProperty('font-weight', 'bold', 'important');
      e.cellElement.style.border = "2px solid red";
    }
  };

  // Cancel DevExtreme focus moves that target non-editable columns (Delete).
  const onFocusedCellChanging = (e) => {
    const cols = e.component?.getVisibleColumns?.();
    if (!cols || e.newColumnIndex == null) return;
    const target = cols[e.newColumnIndex];
    if (target && !target.dataField) {
      e.cancel = true;
    }
  };

  // Custom Tab nav between editable cells. blur+click works for cells that
  // already have a value (DevExtreme commits on blur) but the same approach
  // fails on empty cells — DevExtreme keeps the empty editor focused. Use the
  // editCell API instead: it both commits the previous cell and opens the
  // next, regardless of whether the value is empty.
  const EDITABLE_FIELDS = ['quantity', 'length', 'width', 'height', 'weight', 'description', 'positions'];

  const focusCell = (instance, rowIdx) => {
    const rowEl = instance.getRowElement(rowIdx)?.[0];
    const input = rowEl?.querySelector('.dx-texteditor-input');
    if (input) {
      input.focus();
      if (typeof input.select === 'function') input.select();
    } else {
      // Editor not mounted yet — try once more on the next frame.
      requestAnimationFrame(() => {
        const r = instance.getRowElement(rowIdx)?.[0];
        const i = r?.querySelector('.dx-texteditor-input');
        if (i) { i.focus(); if (typeof i.select === 'function') i.select(); }
      });
    }
  };

  // Attached to DataGrid onKeyDown (not a wrapper-div capture handler).
  // dxEvent.handled = true is the DevExtreme API that prevents its internal
  // Tab processing. The previous onKeyDownCapture + stopPropagation approach
  // broke on deployed builds because stopPropagation on a parent capture
  // handler does NOT suppress listeners registered directly on the target
  // element (dx-texteditor-input). DevExtreme's own Tab handler lives there,
  // so both fired and competed — producing the 3-keystroke sequence observed
  // on the dev environment (focus lost -> current cell selected -> next cell).
  const handleGridKeyDown = (dxEvent) => {
    const e = dxEvent.event;
    if (!e || e.key !== 'Tab') return;

    const instance = dxEvent.component;
    if (!instance) return;

    // Accept both an active text editor (editing) and a focused td (keyboard nav).
    const isEditingInput = !!e.target?.classList?.contains('dx-texteditor-input');
    const td = isEditingInput
      ? e.target.closest?.('td')
      : e.target?.tagName === 'TD' ? e.target : e.target?.closest?.('td');
    const tr = td?.closest?.('tr.dx-data-row');
    const tbody = tr?.parentElement;
    if (!td || !tr || !tbody) return;

    const visibleColumns = instance.getVisibleColumns();
    const tdIdx = Array.from(tr.children).indexOf(td);
    if (tdIdx < 0 || tdIdx >= visibleColumns.length) return;
    const currentColumn = visibleColumns[tdIdx];
    const currentField = currentColumn?.dataField;
    if (!currentField || !EDITABLE_FIELDS.includes(currentField)) return;

    // Derive editable field order from getVisibleColumns() so Tab respects
    // any column reordering the user may have done.
    const editableFields = visibleColumns
      .filter(col => col.dataField && EDITABLE_FIELDS.includes(col.dataField))
      .map(col => col.dataField);

    const currentFieldIdx = editableFields.indexOf(currentField);
    const dataRows = Array.from(tbody.querySelectorAll('tr.dx-data-row'));
    const currentRowIdx = dataRows.indexOf(tr);

    const direction = e.shiftKey ? -1 : 1;
    let nextFieldIdx = currentFieldIdx + direction;
    let nextRowIdx = currentRowIdx;

    if (nextFieldIdx >= editableFields.length) {
      nextFieldIdx = 0;
      nextRowIdx += 1;
    } else if (nextFieldIdx < 0) {
      nextFieldIdx = editableFields.length - 1;
      nextRowIdx -= 1;
    }

    if (nextRowIdx < 0 || nextRowIdx >= instance.getVisibleRows().length) {
      return; // let native Tab escape the grid
    }

    // Tell DevExtreme not to process this Tab — must be set before any async work.
    dxEvent.handled = true;
    e.preventDefault();

    if (isEditingInput) {
      // Parse the typed value into the correct type for this column.
      const rawValue = e.target.value;
      let committedValue;
      if (rawValue === '' || rawValue == null) {
        committedValue = '';
      } else if (currentColumn?.dataType === 'number') {
        const parsed = Number(rawValue);
        committedValue = Number.isNaN(parsed) ? '' : parsed;
      } else {
        committedValue = rawValue;
      }

      const rowKey = instance.getKeyByRowIndex(currentRowIdx);
      const currentList = piecesOptionList;
      const lastRowId = currentList[currentList.length - 1]?.id;
      const wasLastRow = rowKey === lastRowId;
      let newList = currentList.map((item) =>
        item.id === rowKey ? { ...item, [currentField]: committedValue } : item
      );
      if (wasLastRow) {
        const updatedRow = newList.find((item) => item.id === rowKey);
        if (updatedRow && !isRowEmpty(updatedRow)) {
          const newId = Math.max(...newList.map((r) => r.id)) + 1;
          newList = [...newList, createEmptyRow(newId)];
        }
      }

      // flushSync forces the React re-render to complete synchronously so
      // DevExtreme's dataSource prop is updated BEFORE editCell is called.
      // Without this, the async re-render arrives after editCell opens the
      // next editor and DevExtreme closes it — the "cursor flash then focus
      // lost" seen when tabbing to the third cell and beyond.
      flushSync(() => setPiecesOptionList(newList));
      NoticeEmitter.emit('PackagePiecesUpdate', 'update');
    }

    // Open next cell synchronously while focus is still on this tick — any
    // delay would let a fast user's next keystroke land in the old input.
    instance.editCell(nextRowIdx, editableFields[nextFieldIdx]);
    focusCell(instance, nextRowIdx);
  };

  // Strip tab focus from grid chrome: headers, the scroll container, and any
  // button/link inside data rows. Cells themselves stay focusable so DevExtreme's
  // built-in Tab navigation between editable cells still works.
  const onContentReady = (e) => {
    const grid = e.element;
    if (!grid) return;
    grid.querySelectorAll('.dx-header-row td').forEach((cell) => {
      cell.setAttribute('tabindex', '-1');
    });
    grid.querySelectorAll('tr.dx-data-row button, tr.dx-data-row a').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    grid.querySelectorAll('.dx-datagrid-rowsview, .dx-scrollable, .dx-scrollable-container').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
  };

  return (
    <div style={{ width: '100%' }} onBlur={onGridBlur} tabIndex="0">
      <style>{`
        .dx-revert-button { display: none !important; }
        .negative-cell {
          color: red !important;
          font-weight: bold !important;
          border: 1px solid red !important;
        }
      `}</style>
      <Typography variant="body2" color="textSecondary" style={{marginBottom: '16px'}}>
        Define individual pieces by editing the grid below. A new row will be added automatically.
      </Typography>

      <DataGrid
        ref={dataGridRef}
        dataSource={piecesOptionList}
        repaintChangesOnly={true}
        showBorders={true}
        allowColumnReordering={true}
        allowColumnResizing={true}
        keyExpr="id"
        rowAlternationEnabled={true}
        onRowUpdated={onRowUpdated}
        onCellPrepared={onCellPrepared}
        onContentReady={onContentReady}
        onFocusedCellChanging={onFocusedCellChanging}
        onKeyDown={handleGridKeyDown}
        keyboardNavigation={{ enabled: true, editOnKeyPress: true }}
      >
        <Editing
            mode="cell"
            allowUpdating={true}
        />
        <Sorting mode="none" />
        <Column dataField="id" caption="ID" alignment="center" width={100} visible={false} allowEditing={false}/>
        <Column dataField="quantity" caption="Quantity" dataType="number" width={120}>
          <RangeRule min={0} message="Quantity cannot be negative." />
        </Column>
        <Column dataField="length" caption="Length (in)" dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Length cannot be negative." />
        </Column>
        <Column dataField="width" caption="Width (in)" dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Width cannot be negative." />
        </Column>
        <Column dataField="height" caption="Height (in)" dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Height cannot be negative." />
        </Column>
        <Column dataField="weight" caption="Weight (lbs)" dataType="number" alignment="center" width={120}>
          <RangeRule min={0} message="Weight cannot be negative." />
        </Column>
        <Column dataField="description" caption="Description" width={200}/>
        <Column dataField="positions" caption="Skid Positions" dataType="number" width={150}>
          <RangeRule min={0} message="Skid Positions cannot be negative." />
        </Column>
        <Selection mode="single"/>
        <Column caption="Delete" width={100} alignment="center" cellRender={renderDelete} allowEditing={false}/>
      </DataGrid>
    </div>
  );
}

export default DefineIndividualPieces;
